class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderSection(html, event, updateEvent);
      })
      .catch((error) => {
        console.error(error);
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent);
  }

  static renderSection(html, event, updateEvent) {
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.dataset.productCount = sourceCount.dataset.productCount || '';
    container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    const txt = sourceCount.textContent || '';
    const m = txt.match(/of\s+([\d,]+)/) || txt.match(/([\d,]+)/);
    const total = (m && m[1]) || sourceCount.dataset.productCount || sourceCount.dataset.totalCount; /* filtered total first (data-total-count is the unfiltered collection size) */
    document.querySelectorAll('[data-mobile-apply]').forEach((applyBtn) => {
      if (!applyBtn.dataset.applyTemplate || !total) return;
      applyBtn.textContent = (String(total) === '1' && applyBtn.dataset.applyTemplateOne) ? applyBtn.dataset.applyTemplateOne : applyBtn.dataset.applyTemplate.replace('{count}', total);
    });
    /* collection header band: "155 products" follows the filtered total */
    const bandCount = document.querySelector('.collection-hero-b2b__count');
    if (bandCount && total && /^\s*[\d,]+/.test(bandCount.textContent)) bandCount.textContent = bandCount.textContent.replace(/^\s*[\d,]+/, String(total));
    /* search H1 carries the result count "(N)" */
    const h1 = document.querySelector('.search-b2b__title');
    if (h1 && total && /\(\d[\d,]*\)/.test(h1.innerHTML)) h1.innerHTML = h1.innerHTML.replace(/\(\d[\d,]*\)/, '(' + total + ')');
    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    /* a price change inserts the chip row above the slider: remember where the slider sits and scroll the rail
       (its own scroll container) by the difference afterwards, so the handle stays under the pointer (CLS) */
    const heldRange = event && event.target && event.target.closest ? event.target.closest('price-range') : null;
    const heldTop = heldRange ? heldRange.getBoundingClientRect().top : null;
    if (heldRange) requestAnimationFrame(() => {
      const rail = heldRange.closest('.facets-wrapper--sticky');
      const delta = heldRange.getBoundingClientRect().top - heldTop;
      if (rail && Math.abs(delta) > 1 && rail.scrollHeight > rail.clientHeight) rail.scrollTop += delta;
    });
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting', '.plp-mobile-bar__filters', '[data-facets-active-count]'];

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
      if (selector === '[data-facets-active-count]') target.classList.toggle('hidden', source.classList.contains('hidden'));
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    const clean = String(searchParams || '').replace(/^&+|&+$/g, '').replace(/&&+/g, '&');
    history.pushState({ searchParams: clean }, '', `${window.location.pathname}${clean ? '?' + clean : ''}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams();
    formData.forEach((v, k) => {
      if (String(v).trim() === '') return; /* no empty price params in the URL */
      if (k === 'sort_by' && (v === 'manual' || v === 'relevance')) return; /* the default sort is implicit */
      params.append(k, v);
    });
    return params.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.filter(Boolean).join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  /* Two-handle price slider + From/To inputs + quick ranges, all bound to Shopify's native
     filter.v.price.gte / .lte params (markup server-rendered in snippets/price-facet.liquid). */
  constructor() {
    super();
    this.inputs = this.querySelectorAll('input.field__input');
    this.slider = this.querySelector('[data-price-slider]');
    if (this.inputs.length !== 2) return;
    this.min = this.inputs[0];
    this.max = this.inputs[1];
    this.cap = Math.ceil(parseFloat((this.slider && this.slider.dataset.max) || this.max.getAttribute('data-max')) || 100); /* whole dollars: a max at the cap means "no upper bound" */
    this.inputs.forEach((el) => {
      el.addEventListener('change', this.onInputChange.bind(this));
      el.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    if (this.slider) this.bindSlider();
    this.querySelectorAll('[data-price-preset]').forEach((b) => b.addEventListener('click', this.onPreset.bind(this)));
    this.paint();
  }

  num(el) { const n = parseFloat(String(el.value).replace(/[^0-9.]/g, '')); return isNaN(n) ? NaN : n; } /* "1,171.00" -> 1171 */

  values() {
    const lo = Math.max(0, Math.min(this.num(this.min) || 0, this.cap));
    const hiRaw = this.num(this.max);
    const hi = isNaN(hiRaw) ? this.cap : Math.max(lo, Math.min(hiRaw, this.cap));
    return { lo, hi };
  }

  setValues(lo, hi, submit) {
    lo = Math.max(0, Math.min(Math.round(lo), this.cap));
    hi = Math.max(lo, Math.min(Math.round(hi), this.cap));
    this.min.value = lo > 0 ? String(lo) : '';
    this.max.value = hi < this.cap ? String(hi) : '';
    this.paint();
    if (submit) this.submit();
  }

  /* track position <-> price: a power curve so the common sub-$100 range owns most of the track even when one
     outlier lifts the collection's maximum into the thousands */
  toPct(v) { return this.cap ? Math.pow(Math.max(0, v) / this.cap, 1 / PriceRange.CURVE) * 100 : 0; }
  fromPct(t) { return this.cap * Math.pow(Math.min(Math.max(t, 0), 1), PriceRange.CURVE); }

  paint() {
    const { lo, hi } = this.values();
    if (this.slider) {
      const pct = (v) => this.toPct(v);
      const fill = this.slider.querySelector('[data-fill]');
      if (fill) { fill.style.left = pct(lo) + '%'; fill.style.width = Math.max(0, pct(hi) - pct(lo)) + '%'; }
      this.slider.querySelectorAll('[data-handle]').forEach((h) => {
        const v = h.dataset.handle === 'lo' ? lo : hi;
        h.style.left = pct(v) + '%';
        h.setAttribute('aria-valuenow', Math.round(v));
        h.setAttribute('aria-valuetext', '$' + Math.round(v));
        const label = h.querySelector('[data-label]');
        if (label) label.textContent = '$' + Math.round(v);
        h.classList.toggle('is-top', h.dataset.handle === 'lo' && lo >= this.cap - 1);
      });
      this.slider.classList.remove('is-pending');
    }
    const noMin = this.min.value === '';
    const noMax = this.max.value === '';
    this.querySelectorAll('[data-price-preset]').forEach((b) => {
      const pLo = parseFloat(b.dataset.min) || 0;
      const pHi = b.dataset.max === '' ? null : parseFloat(b.dataset.max);
      const on = !(noMin && noMax) && pLo === lo && (pHi === null ? noMax : pHi === hi);
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  submit() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      const host = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      const evt = { target: this.max, srcElement: this.max, preventDefault() {} };
      if (host && typeof host.onSubmitHandler === 'function') host.onSubmitHandler(evt);
    }, 250);
  }

  onInputChange(event) {
    let lo = Math.max(0, Math.min(this.num(this.min) || 0, this.cap));
    const hiRaw = this.num(this.max);
    let hi = isNaN(hiRaw) ? this.cap : Math.min(hiRaw, this.cap);
    if (hi < lo) {
      if (event && event.currentTarget === this.max) lo = 0; else hi = this.cap; /* the side just edited wins */
    }
    this.setValues(lo, hi, true);
  }

  onPreset(event) {
    const b = event.currentTarget;
    const on = b.getAttribute('aria-pressed') === 'true';
    if (on) { this.min.value = ''; this.max.value = ''; this.paint(); this.submit(); return; }
    const lo = parseFloat(b.dataset.min) || 0;
    const hi = b.dataset.max === '' ? this.cap : parseFloat(b.dataset.max);
    this.setValues(lo, hi, true);
  }

  onKeyDown(event) {
    if (event.metaKey || event.ctrlKey) return;
    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape|Home|End/;
    if (!event.key.match(pattern)) event.preventDefault();
    if (event.key === 'Enter') { event.preventDefault(); this.onInputChange(event); }
  }

  bindSlider() {
    const slider = this.slider;
    const handles = slider.querySelectorAll('[data-handle]');
    const toValue = (clientX) => {
      const r = slider.getBoundingClientRect();
      const x = Math.min(Math.max(clientX - r.left, 0), r.width);
      return r.width ? this.fromPct(x / r.width) : 0;
    };
    let active = null;
    const move = (e) => {
      if (!active) return;
      const v = toValue(e.clientX);
      const { lo, hi } = this.values();
      if (active.dataset.handle === 'lo') this.setValues(Math.min(v, hi), hi, false);
      else this.setValues(lo, Math.max(v, lo), false);
      e.preventDefault();
    };
    const up = () => {
      if (!active) return;
      active.classList.remove('is-dragging');
      active = null;
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', up);
      this.submit();
    };
    handles.forEach((h) => {
      h.addEventListener('pointerdown', (e) => {
        active = h; h.classList.add('is-dragging'); h.focus({ preventScroll: true });
        document.addEventListener('pointermove', move);
        document.addEventListener('pointerup', up);
        document.addEventListener('pointercancel', up);
        e.preventDefault();
      });
      h.addEventListener('keydown', (e) => {
        const { lo, hi } = this.values();
        const isLo = h.dataset.handle === 'lo';
        const base = isLo ? lo : hi;
        const step = e.shiftKey ? Math.max(5, Math.round(base / 10)) : Math.max(1, Math.round(base / 50)); /* steps grow with the price */
        let v = base;
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') v += step;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') v -= step;
        else if (e.key === 'Home') v = isLo ? 0 : lo;
        else if (e.key === 'End') v = isLo ? hi : this.cap;
        else return;
        e.preventDefault();
        if (isLo) this.setValues(Math.min(v, hi), hi, false); else this.setValues(lo, Math.max(v, lo), false);
        this.submit();
      });
    });
    /* tap on the track jumps the nearest handle */
    slider.addEventListener('pointerdown', (e) => {
      if (e.target.closest('[data-handle]')) return;
      const v = toValue(e.clientX);
      const { lo, hi } = this.values();
      if (Math.abs(v - lo) <= Math.abs(v - hi)) this.setValues(v, hi, true); else this.setValues(lo, v, true);
    });
  }
}

PriceRange.CURVE = 2.5;
customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    event.stopPropagation(); /* a Clear link inside a <summary> must not toggle the group */
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

/* mobile filter drawer: Escape closes it and returns focus to the opener */
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  const open = document.querySelector('.mobile-facets__disclosure[open]');
  if (!open) return;
  open.removeAttribute('open');
  const opener = open.querySelector('summary') || document.querySelector('[data-mobile-facets-open]');
  if (opener) opener.focus();
});
